export const SMS_HEADING_TEXT = 'SMS';
export const SMS_HEADING_TEXT_WITH_WHATSAPP = 'SMS & WhatsApp';

export const sideNavLinks = ({
  hasSmsChannel,
  useFilteredTracking,
  isWhatsAppEnabled,
}: { hasSmsChannel: boolean; useFilteredTracking: boolean; isWhatsAppEnabled: boolean }) => [
  {
    name: 'Project',
    pathname: '/settings/project',
    hash: 'project',
  },
  {
    name: 'Messaging',
    pathname: '/settings/project',
    hash: 'messaging',
  },
  {
    name: 'Email',
    pathname: '/settings/project',
    hash: 'email',
  },
  ...(hasSmsChannel
    ? [
        {
          name: isWhatsAppEnabled ? SMS_HEADING_TEXT_WITH_WHATSAPP : SMS_HEADING_TEXT,
          pathname: '/settings/project',
          hash: 'sms',
        },
      ]
    : []),
  {
    name: 'Content',
    pathname: '/settings/project',
    hash: 'content',
  },
  {
    name: 'Audience',
    pathname: '/settings/project',
    hash: 'audience',
  },
  {
    name: 'Insights',
    pathname: '/settings/project',
    hash: 'insights',
  },
  ...(useFilteredTracking
    ? [
        {
          name: 'Tracking',
          pathname: '/settings/project',
          hash: 'tracking',
        },
      ]
    : []),
];

export const PROJECT_FIELD_NAMES = {
  NAME: 'name',
  LOGO_URL: 'logoUrl',
  TIME_ZONE: 'timeZone',
  PHYSICAL_ADDRESS: 'physicalAddress',
} as const;

export const MESSAGING_FIELD_NAMES = {
  LABELS: 'labels',
  GROUP_RECURRING_CAMPAIGNS: 'groupRecurringCampaigns',
} as const;

export const TRACKING_FIELD_NAMES = {
  USE_FILTERED_TRACKING: 'useFilteredTracking',
} as const;

export const EMAIL_FIELD_NAMES = {
  FIRST_NAME_FIELD: 'firstNameField',
  LAST_NAME_FIELD: 'lastNameField',
  USE_DYNAMIC_SENDERS: 'useDynamicSenders',
  /*
    ENABLE_SENDING_DOMAIN is only used on the client side, to handle the
    checkbox which shows/hides the sending domain field.
   */
  ENABLE_SENDING_DOMAIN: 'ENABLE_SENDING_DOMAIN',
  SENDING_DOMAIN: 'sendingDomain',
  ADD_UNSUBSCRIBE: 'addUnsubscribe',
  USE_DOUBLE_OPT_OUT: 'useDoubleOptOut',
  CUSTOM_UNSUBSCRIBE_SNIPPET: 'customUnsubscribeSnippet',
  HOSTED_UNSUBSCRIBE_URL: 'hostedUnsubscribeUrl',
  USE_HOSTED_UNSUBSCRIBE_URL_IN_LIST_UNSUBSCRIBE_HEADER:
    'useHostedUnsubscribeUrlInListUnsubscribeHeader',
  SUBSCRIBE_SUCCESS_URL: 'subscribeSuccessUrl',
  ENABLE_GEO_IP_LOOKUP: 'enableGeoIPLookup',
  ENABLE_EMAIL_CLICK_IP_UPDATE: 'enableEmailClickIPUpdate',
} as const;

export const SMS_FIELD_NAMES = {
  /*
    SMS_OPT_OUT_INSTRUCTIONS is only used on the client side, to maintain the
    state of the SMS Opt Out dropdown, which maps to isSMSDisclaimerOnAllMessages
    and disableSMSDisclaimer on the backend.
   */
  SMS_OPT_OUT_INSTRUCTIONS: 'SMS_OPT_OUT_INSTRUCTIONS',
  IS_SMS_DISCLAIMER_ON_ALL_MESSAGES: 'isSMSDisclaimerOnAllMessages',
  DISABLE_SMS_DISCLAIMER: 'disableSMSDisclaimer',
  SMS_DISCLAIMER: 'smsDisclaimer',
  SMS_SHORTLINKS_MIN_LENGTH_MARKETING: 'smsShortlinksMinLengthMarketing',
  SMS_SHORTLINKS_MIN_LENGTH_TRANSACTIONAL: 'smsShortlinksMinLengthTransactional',
} as const;

export const CONTENT_FIELD_NAMES = {
  WYSIWYG_EDITOR_MODE: 'wysiwygEditorMode',
  HMAC_SECRET: 'HMACSecret',
  LOCALES: 'locales',
  MAPPED_LOCALES: 'mappedLocales',
  TEMPLATE_DEFAULT_LOCALE_MODIFIABLE: 'templateDefaultLocaleModifiable',
  OVERRIDE_CONTENT_NOT_FOUND_MARKETING: 'overrideContentNotFoundMarketing',
  OVERRIDE_CONTENT_NOT_FOUND_TRANSACTIONAL: 'overrideContentNotFoundTransactional',
} as const;

export const AUDIENCE_FIELD_NAMES = {
  ENABLE_ANON_USER_TRACKING: 'enableAnonUserTracking',
} as const;

export const INSIGHTS_FIELD_NAMES = {
  SHOW_REVENUE: 'showRevenue',
  ENABLE_IMPROVED_TRACKING: 'enableImprovedTracking',
} as const;

export const FIELD_NAMES = {
  /** Ordering here and above matches the UI */
  ...PROJECT_FIELD_NAMES,
  ...MESSAGING_FIELD_NAMES,
  ...EMAIL_FIELD_NAMES,
  ...SMS_FIELD_NAMES,
  ...CONTENT_FIELD_NAMES,
  ...AUDIENCE_FIELD_NAMES,
  ...INSIGHTS_FIELD_NAMES,
  ...TRACKING_FIELD_NAMES,
};

export const FIELD_NAME_LABELS = {
  [PROJECT_FIELD_NAMES.NAME]: 'Project name',
  [PROJECT_FIELD_NAMES.LOGO_URL]: 'Project logo',
  [PROJECT_FIELD_NAMES.TIME_ZONE]: 'Time zone',
  [PROJECT_FIELD_NAMES.PHYSICAL_ADDRESS]: 'Physical mailing address',
  [MESSAGING_FIELD_NAMES.LABELS]: 'Labels',
  [MESSAGING_FIELD_NAMES.GROUP_RECURRING_CAMPAIGNS]: 'Group recurring campaigns',
  [EMAIL_FIELD_NAMES.FIRST_NAME_FIELD]: 'Email sender first name',
  [EMAIL_FIELD_NAMES.LAST_NAME_FIELD]: 'Email sender last name',
  [EMAIL_FIELD_NAMES.USE_DYNAMIC_SENDERS]: 'Email sender use dynamic senders',
  [EMAIL_FIELD_NAMES.ENABLE_SENDING_DOMAIN]: 'Email sender enable sending domain',
  [EMAIL_FIELD_NAMES.SENDING_DOMAIN]: 'Email sender sending domain',
  [EMAIL_FIELD_NAMES.ADD_UNSUBSCRIBE]: 'Email sender add unsubscribe',
  [EMAIL_FIELD_NAMES.USE_DOUBLE_OPT_OUT]: 'Email sender use double opt-out',
  [EMAIL_FIELD_NAMES.CUSTOM_UNSUBSCRIBE_SNIPPET]: 'Email sender custom unsubscribe snippet',
  [EMAIL_FIELD_NAMES.HOSTED_UNSUBSCRIBE_URL]: 'Email sender hosted unsubscribe URL',
  [EMAIL_FIELD_NAMES.USE_HOSTED_UNSUBSCRIBE_URL_IN_LIST_UNSUBSCRIBE_HEADER]:
    'Email sender use hosted unsubscribe URL in list unsubscribe header',
  [EMAIL_FIELD_NAMES.SUBSCRIBE_SUCCESS_URL]: 'Email sender subscribe success URL',
  [SMS_FIELD_NAMES.SMS_OPT_OUT_INSTRUCTIONS]: 'SMS opt out instructions',
  [SMS_FIELD_NAMES.SMS_DISCLAIMER]: 'SMS opt out disclaimer',
  [SMS_FIELD_NAMES.SMS_SHORTLINKS_MIN_LENGTH_MARKETING]: 'SMS shortlinks marketing length ',
  [SMS_FIELD_NAMES.SMS_SHORTLINKS_MIN_LENGTH_TRANSACTIONAL]: 'SMS shortlinks transactional length ',
  [CONTENT_FIELD_NAMES.WYSIWYG_EDITOR_MODE]: 'WYSIWYG editor mode',
  [CONTENT_FIELD_NAMES.HMAC_SECRET]: 'HMAC secret',
  [CONTENT_FIELD_NAMES.LOCALES]: 'Locales',
  [CONTENT_FIELD_NAMES.MAPPED_LOCALES]: 'Mapped locales',
  [CONTENT_FIELD_NAMES.TEMPLATE_DEFAULT_LOCALE_MODIFIABLE]: 'Template default locale modifiable',
  [CONTENT_FIELD_NAMES.OVERRIDE_CONTENT_NOT_FOUND_MARKETING]:
    'Marketing locale override preference',
  [CONTENT_FIELD_NAMES.OVERRIDE_CONTENT_NOT_FOUND_TRANSACTIONAL]:
    'Transactional locale override preference',
  [AUDIENCE_FIELD_NAMES.ENABLE_ANON_USER_TRACKING]: 'Enable anonymous user tracking',
  [EMAIL_FIELD_NAMES.ENABLE_GEO_IP_LOOKUP]: 'Overwrite location fields',
  [EMAIL_FIELD_NAMES.ENABLE_EMAIL_CLICK_IP_UPDATE]: 'Overwrite IP field',
  [INSIGHTS_FIELD_NAMES.SHOW_REVENUE]: 'Show revenue',
  [INSIGHTS_FIELD_NAMES.ENABLE_IMPROVED_TRACKING]: 'Enable improved tracking',
  [TRACKING_FIELD_NAMES.USE_FILTERED_TRACKING]: 'In-app click and close event tracking',
};

export enum SmsOptOutValues {
  First = 'first',
  Every = 'every',
  Never = 'never',
}

export const smsOptOutOptions = (isBossOrSalesBossLevel?: boolean) => {
  const options = [
    {
      id: 'first',
      label: 'Only with the first SMS campaign to each contact',
      value: SmsOptOutValues.First,
    },
    { id: 'every', label: 'With every SMS campaign', value: SmsOptOutValues.Every },
  ];
  return isBossOrSalesBossLevel
    ? [...options, { id: 'never', label: 'Never (Boss only)', value: SmsOptOutValues.Never }]
    : options;
};

export const SHORTLINKS_MIN_VALUE = 5;
export const SHORTLINKS_MAX_VALUE = 20;

export const NEW_TAB_PROPS = { target: '_blank', rel: 'noopener noreferrer' };

export const MAX_IMAGE_SIZE_IN_MB = 3;
export const MAX_IMAGE_SIZE = MAX_IMAGE_SIZE_IN_MB * 1024 * 1024;
export const MIN_IMAGE_SIZE = 0;
export const ACCEPTS_TYPES = {
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/gif': ['.gif'],
};

/**
 * Used as a placeholder for URL fields.
 */
export const PLACEHOLDER_URL = 'https://example.com';

/**
 * Fields that have custom API endpoints for mutating their data.
 */
export const FIELDS_WITH_CUSTOM_API_ENDPOINTS = [
  PROJECT_FIELD_NAMES.LOGO_URL,
  MESSAGING_FIELD_NAMES.LABELS,
];
